import { parseSpeakerTurns, type SpeakerAssignment } from "@rosc/contracts";
import type { TranscriptionStreamText } from "@/hooks/transcriber/types";

// One line of the live transcript while recording. Diarized segments arrive
// as "Speaker 0: …" (the transcriber's stored format, which note generation
// reads); the recorder shows the speaker as a label instead of that prefix.
export type LiveTranscriptLine = {
  key: string;
  startTime: number;
  // null: undiarized text (no label).
  speaker: number | null;
  assignment: SpeakerAssignment | null;
  text: string;
  // Show the speaker label: the first line of each speaker's run.
  showSpeaker: boolean;
  // In-progress words (display only, never saved).
  partial: boolean;
  // The finished segment it comes from (in-progress words: after the last).
  segment: number;
};

// Finished segments, then the in-progress words, split at speaker changes.
// The in-progress text starts where the last segment began (as on iOS).
export const liveTranscriptLines = (
  segments: TranscriptionStreamText[],
  partial = "",
): LiveTranscriptLine[] => {
  const lines: LiveTranscriptLine[] = [];
  const add = (
    text: string,
    startTime: number,
    key: string,
    isPartial: boolean,
    segment: number,
  ): void => {
    for (const [index, turn] of parseSpeakerTurns(text).entries()) {
      if (!turn.text) continue;
      const previous = lines.at(-1);
      lines.push({
        key: `${key}-${index}`,
        startTime,
        speaker: turn.speaker,
        assignment: turn.assignment,
        text: turn.text,
        showSpeaker:
          turn.speaker !== null &&
          (previous?.speaker !== turn.speaker ||
            // A role found later in the same run is worth showing.
            (!!turn.assignment &&
              previous?.assignment?.role !== turn.assignment.role)),
        partial: isPartial,
        segment,
      });
    }
  };
  for (const [index, segment] of segments.entries())
    add(
      segment.text,
      segment.startTime,
      `${segment.startTime}-${index}`,
      false,
      index,
    );
  if (partial.trim())
    add(
      partial,
      segments.at(-1)?.startTime ?? 0,
      "partial",
      true,
      segments.length,
    );
  return lines;
};

export type LiveTranscriptItem<Gap> =
  | { kind: "line"; line: LiveTranscriptLine; index: number }
  | { kind: "gap"; gap: Gap };

// The lines with each gap (a part the live transcript missed) placed before
// the first line from a segment that arrived after it, or at the end.
export const withGaps = <Gap extends { afterIndex: number }>(
  lines: LiveTranscriptLine[],
  gaps: readonly Gap[],
): LiveTranscriptItem<Gap>[] => {
  const pending = [...gaps].sort((a, b) => a.afterIndex - b.afterIndex);
  const items: LiveTranscriptItem<Gap>[] = [];
  for (const [index, line] of lines.entries()) {
    while (pending.length && (pending[0] as Gap).afterIndex <= line.segment)
      items.push({ kind: "gap", gap: pending.shift() as Gap });
    items.push({ kind: "line", line, index });
  }
  for (const gap of pending) items.push({ kind: "gap", gap });
  return items;
};
